import React, { useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
    Box,
    Dialog,
    IconButton,
    Link,
    Slide,
    Typography,
} from "@mui/material";
import { usePagesTranslation } from "../../../../pages/locales";
import { getIapResultUserMessage } from "../_stubs/iapFeedbackMessages";
import { LEGAL_PATHS, openLegalWebPage } from "../_stubs/legalWebLinks";
import { useSubscription } from "../lib/SubscriptionContext";
import CreditPurchaseSection from "./CreditPurchaseSection";

import { getPremiumBenefitLines } from "../lib/premiumBenefits";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

const legalLinkSx = {
    verticalAlign: "baseline",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8125rem",
    fontFamily: systemFont,
    color: "#3BA666",
    border: "none",
    bgcolor: "transparent",
    p: 0,
    minHeight: 44,
    display: "inline-flex",
    alignItems: "center",
};

function Transition(props) {
    return <Slide direction="up" {...props} />;
}

/**
 * Modal paywall — credit packs (Master Pass / Pocket / Pro).
 */
export default function PaywallSheet({ open, onClose, onUnlocked }) {
    const { t } = usePagesTranslation();
    const { credits, purchaseCredits, isProcessing, notifyAccessRefresh } = useSubscription();
    const [inlineError, setInlineError] = useState("");

    const handleDismiss = () => {
        if (isProcessing) return;
        setInlineError("");
        onClose();
    };

    const handlePurchase = async (packageKey) => {
        setInlineError("");
        try {
            const result = await purchaseCredits(packageKey);
            if (result?.cancelled) {
                return;
            }
            if (result?.success && result?.pendingBackendSync) {
                notifyAccessRefresh();
                setInlineError(
                    t("native.credits.signInAfterPurchase", {
                        defaultValue:
                            "Purchase recorded. Sign in to sync credits to your account.",
                    }),
                );
                onUnlocked?.();
                onClose();
                return;
            }
            if (result?.success) {
                notifyAccessRefresh();
                onUnlocked?.();
                onClose();
                return;
            }
            if (result?.error === "login_required") {
                setInlineError(
                    t("native.credits.signInAfterPurchase", {
                        defaultValue:
                            "Purchase recorded. Sign in to sync credits to your account.",
                    }),
                );
                return;
            }
            const msg = getIapResultUserMessage(result, t, "purchase");
            if (msg) {
                setInlineError(msg);
            }
        } catch {
            /* purchase resolves */
        }
    };

    const paywallMode = credits.needsTopUp ? "topup" : "intro";

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleDismiss}
            TransitionComponent={Transition}
            sx={{
                "& .MuiDialog-paper": {
                    bgcolor: "#f2f2f7",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100dvh",
                    pt: "max(env(safe-area-inset-top, 0px), 12px)",
                    pb: "env(safe-area-inset-bottom, 0px)",
                    px: 2,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <IconButton
                        type="button"
                        aria-label={t("btn.close", { defaultValue: "Close" })}
                        onClick={handleDismiss}
                        disabled={isProcessing}
                        sx={{ minWidth: 44, minHeight: 44 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </Box>

                <Typography
                    component="h2"
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        mb: 1,
                        color: "#0f1419",
                    }}
                >
                    {t("native.paywall.title", {
                        defaultValue: "Unlock Sliding Puzzle Quest",
                    })}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "1rem",
                        lineHeight: 1.45,
                        color: "rgba(15, 23, 42, 0.6)",
                        mb: 3,
                    }}
                >
                    {t("native.paywall.subtitle", {
                        defaultValue:
                            "Play every grid size and difficulty, save progress, and access the full library.",
                    })}
                </Typography>

                <Box
                    sx={{
                        bgcolor: "#ffffff",
                        borderRadius: "14px",
                        p: 2.5,
                        mb: 3,
                        boxShadow: "0 4px 20px rgba(15, 23, 42, 0.08)",
                    }}
                >
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                        {getPremiumBenefitLines(t).map((line) => (
                            <Typography
                                key={line}
                                component="li"
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "0.9375rem",
                                    lineHeight: 1.5,
                                    color: "#0f1419",
                                    mb: 1,
                                }}
                            >
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {inlineError ? (
                    <Typography
                        sx={{
                            fontFamily: systemFont,
                            fontSize: "0.875rem",
                            color: "#b91c1c",
                            mb: 2,
                            lineHeight: 1.45,
                        }}
                    >
                        {inlineError}
                    </Typography>
                ) : null}

                <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1.5, pb: 1 }}>
                    <CreditPurchaseSection
                        mode={paywallMode}
                        creditBalance={credits.creditBalance}
                        balanceLoading={credits.balanceLoading}
                        packagesLoading={credits.packagesLoading}
                        isProcessing={isProcessing}
                        isIapAvailable={credits.isIapAvailable}
                        onPurchase={handlePurchase}
                        getPackagePrice={credits.getPackagePrice}
                        getCreditsForPackage={credits.getCreditsForPackage}
                    />
                </Box>

                <Box sx={{ width: "100%", mt: 2, pb: 2, flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: systemFont,
                            fontSize: "0.6875rem",
                            lineHeight: 1.5,
                            color: "rgba(15, 23, 42, 0.55)",
                            textAlign: "center",
                            mb: 1.5,
                            px: 0.5,
                        }}
                    >
                        {t("native.credits.consumableDisclosure", {
                            defaultValue:
                                "Credits are consumable in-app purchases. They do not auto-renew. Unused credits remain in your account until used.",
                        })}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Link
                            component="button"
                            type="button"
                            onClick={() => void openLegalWebPage(LEGAL_PATHS.privacy)}
                            sx={legalLinkSx}
                        >
                            {t("native.purchase.privacyPolicy", { defaultValue: "Privacy Policy" })}
                        </Link>
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: systemFont,
                                fontSize: "0.75rem",
                                color: "rgba(15, 23, 42, 0.45)",
                            }}
                            aria-hidden
                        >
                            ·
                        </Typography>
                        <Link
                            component="button"
                            type="button"
                            onClick={() => void openLegalWebPage(LEGAL_PATHS.terms)}
                            sx={legalLinkSx}
                        >
                            {t("native.purchase.termsOfUse", { defaultValue: "Terms of Use" })}
                        </Link>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}
